import { createStore, createStyleSet } from 'botframework-webchat';
import classNames from 'classnames';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { Middleware } from 'redux';
import WebChat from './WebChat';
import { appConfig } from '../../../utils/app.config';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './MinimizableWebChat.css';

//for VA Specific font family
const CHAT_FONT_FAMILY ="'Century Gothic', CenturyGothic, AppleGothic, 'Segoe UI', Arial, sans-serif";

function MinimizableWebChat({ bot = "B0002" }) {
  const [loaded, setLoaded] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const [newMessage, setNewMessage] = useState(false);
  const [side, setSide] = useState('right');
  const [token, setToken] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [sendMessage, setSendMessage] = useState(false); // Track voice state
  const [message, setMessage] = useState('');
  const [isListening, setIsListening] = useState(false); // Track if listening to speech
  const [sessionExpired, setSessionExpired] = useState(false);

  const [isInputDisabled, setIsInputDisabled] = useState(false);
  // const [showBotInfo, setShowBotInfo] = useState(false);

  // --- Add: PII & Profanity detection helpers ---
  const PII_PATTERNS: RegExp[] = [
    // 1) Email (generic)
    /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i,

    // 2) UK phone numbers (mobile + landline)
    // Covers: +44 7xxx xxx xxx, 07xxx xxxxxx, 01/02/03 area codes, with optional (0), spaces, dashes, parentheses
    /\b(?:(?:\+?44\s?(?:\(0\)\s?)?)|0)\s?(?:7\d{3}|\d{2,4})[\s\-]?\d{3,4}[\s\-]?\d{3,4}\b/,

    // 3) UK postcodes (strict-ish, includes GIR 0AA and BFPO patterns)
    // Normalizes most real-world formats, case-insensitive
    /\b(?:GIR\s?0AA|BFPO\s?\d{1,4}|[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2})\b/i,

    // 4) UK National Insurance number (NI)
    // Format: QQ 12 34 56 C; excludes invalid prefixes (BG, GB, KN, NK, NT, TN, ZZ)
    /\b(?!(?:BG|GB|KN|NK|NT|TN|ZZ))([A-CEGHJ-PR-TW-Z]{2})\s?\d{2}\s?\d{2}\s?\d{2}\s?[A-D]\b/i,

    // 5) NHS number (10 digits) — with optional spacing: 123 456 7890 or 1234567890
    /\b\d{3}\s?\d{3}\s?\d{4}\b/,

    // 6) UK IBAN (simplified): GB + 2 digits (check) + 4 letters (bank) + 14 digits
    /\bGB\d{2}[A-Z]{4}\d{14}\b/i,

    // 7) Long digit sequences (potential card/ID) — conservative; tune per policy
    // Matches 13–19 digits with optional spaces/dashes
    /\b(?:\d[ -]?){13,19}\b/,
  ];

  const PROFANITY_WORDS = [
    // Keep your curated list (all lowercase)
    "stupid",
    "rude",
    "useless",
    "pagal",
    "senseless",
    "moron",
    "nonsense",
    "idiot",
    "mannerless",
    "shameless",
    "hell",
    "dumb",
    "rascal",
    "bloody"
  ];

  function containsPII(text: string): boolean {
    return PII_PATTERNS.some((rx) => rx.test(text));
  }

  function containsProfanity(text: string): boolean {
    const lowered = text.toLowerCase();
    return PROFANITY_WORDS.some((w) =>
      new RegExp(`\\b${w}\\b`, "i").test(lowered)
    );
  }

  // Middleware for Redux store
  const middleware: Middleware = ({ dispatch }) => (next) => (action: any) => {
    // Add (optional): timing constants
    const DISABLE_MS = 15000; // 15 seconds disable window
    let reenableTimer: number | undefined;
    
    
    if (action.type === "WEB_CHAT/SEND_MESSAGE" || action.type === "DIRECT_LINE/POST_ACTIVITY") {
      // which text is clicked-- to check get string
      const clickedText = action.payload?.text || action.payload?.activity?.text || "";

      setTimeout(() => {
        // 1. find all the active options and adaptive buttons of the chat
        const allButtons = document.querySelectorAll(".webchat__hero-card__button, .ac-pushButton");
        allButtons.forEach((btn: any) => {
          const buttonText = btn.innerText || btn.textContent || "";
          btn.disabled = true;
          btn.style.pointerEvents = 'none';
          // 2. If the button’s text matches the text that was clicked, assign it a different class.
          if (clickedText && buttonText.trim().includes(clickedText.trim())) {
            btn.classList.add("btn-freeze-status-active"); // for Selected button 
          } else {
            btn.classList.add("btn-freeze-status-disabled"); // for non selected - freeze class
          }
        });
      }, 100);
    }
    // ✅ 2.a OUTGOING check: user is sending a message
    
    if (
      action.type === "WEB_CHAT/SEND_MESSAGE" ||
      (action.type === "DIRECT_LINE/POST_ACTIVITY" &&
        action.payload?.activity?.type === "message")
    ) {
      const text: string =
        action.payload?.activity?.text ?? action.payload?.text ?? "";
      if (text) {
        const isPII = containsPII(text);
        const isProfane = containsProfanity(text);
        if (isPII || isProfane) {
          // 1) Block the send
          // (Simply do not call next(action))
          // 2) Disable the send box
          setIsInputDisabled(true);
          dispatch({
            type: "WEB_CHAT/SET_DISABLED",
            payload: { disabled: true },
          });
          // Showing a friendly warning bubble via a client-only activity
          dispatch({
            type: "DIRECT_LINE/INCOMING_ACTIVITY",
            payload: {
              activity: {
                type: "message",
                from: { id: "bot", role: "bot", name: "System" },
                text: isPII
                  ? "⚠️ Please avoid sharing personal Data (email/phone/card/ID). Input has been temporarily disabled."
                  : "⚠️ Please avoid inappropriate language. Input has been temporarily disabled.",
                timestamp: new Date().toISOString(),
              },
            },
          });
          //disable tesxt
          // setInputBlocked(true);
          // setTimeout(() => {
          //   setInputBlocked(false)
          // }, 5000);
          // 3) Auto re-enable after timeout
          if (reenableTimer) {
            window.clearTimeout(reenableTimer);
          }
          reenableTimer = window.setTimeout(() => {
            setIsInputDisabled(false);
            dispatch({
              type: "WEB_CHAT/SET_DISABLED",
              payload: { disabled: false },
            });
          }, DISABLE_MS) as unknown as number;
          return; // ⛔️ Do not forward the original send action
        }
      }
    }

    if (action.type === "DIRECT_LINE/CONNECTION_STATUS_UPDATE") {
      console.log("DIRECT_LINE STATUS", action.payload.connectionStatus);
    }
    if(action.type?.includes("DIRECT_LINE/POST_ACTIVITY_REJECTED")){
      console.log("POST_ACTIVITY_REJECTED:", action.type, action.payload);
    }
    if (
      action.type?.includes("REJECT") ||
      action.type?.includes("FAIL") ||
      action.type?.includes("ERROR")
    ) {
      console.log(
        "ERROR ACTION:",
        action.type,
        action.payload
      );
    }
    if (action.type === "DIRECT_LINE/UPDATE_CONNECTION_STATUS" || action.type === "DIRECT_LINE/POST_ACTIVITY_REJECTED") {
      console.log("DIRECT_LINE STATUS", action.payload.connectionStatus);
      console.log("POST_ACTIVITY_REJECTED", action.payload);
    
      const status = action.payload?.error?.status || action.payload?.status;
      const isExpiredByConnection = action.type === "DIRECT_LINE/UPDATE_CONNECTION_STATUS" && action.payload?.connectionStatus === 3;
      const isExpiredBy401 = action.type === "DIRECT_LINE/POST_ACTIVITY_REJECTED" && status === 401;
      
      if ((isExpiredByConnection || isExpiredBy401) && !sessionExpired) {
        setSessionExpired(true);
        setIsInputDisabled(true);
      
        // dispatch({
        //   type: "DIRECT_LINE/INCOMING_ACTIVITY",
        //   payload: {
        //     activity: {
        //       type: "message",
        //       from: {
        //         id: "bot",
        //         role: "bot",
        //         name: "System"
        //       },
        //       text:
        //         "⚠️ Your session has expired. Please refresh the page and start a new chat session.",
        //       timestamp: new Date().toISOString()
        //     }
        //   }
        // });
      
        dispatch({
          type: "WEB_CHAT/SET_DISABLED",
          payload: {
            disabled: true
          }
        });
        // alert("Your session has expired. Please refresh the page and start a new conversation.");
      }
    }

    if (action.type === "DIRECT_LINE/CONNECT_FULFILLED") {
      dispatch({
        type: "WEB_CHAT/SEND_EVENT",
        payload: {
          name: "webchat/join",
          value: {
            language: window.navigator.language,
          },
        },
      });
    } else if (action.type === "DIRECT_LINE/INCOMING_ACTIVITY") {
      if (action.payload.activity.from.role === "bot") {
        setNewMessage(true);
      }
    }

    return next(action);
  };

  const store = useMemo(() => createStore({}, middleware), []);

  const styleSet = useMemo(() => createStyleSet({
    backgroundColor: 'Transparent'
  }), []);

  //   styleSet.textContent = {
  //     ...styleSet.textContent,
  //     fontFamily: "'Comic Sans MS', 'Arial', sans-serif",
  //     fontWeight: 'bold'
  //  };
  const styleOptions = useMemo(
    () => ({
      primaryFont: CHAT_FONT_FAMILY,
      monospaceFont: CHAT_FONT_FAMILY,
      //adaptive card font
      adaptiveCardsHostConfig: {
        fontFamily: CHAT_FONT_FAMILY,
        fontSizes: {
          small: 12,
          default: 14,
          medium: 16,
          large: 18,
          extraLarge: 22,
        },
        fontWeights: {
          lighter: 300,
          default: 500,
          bolder: 700,
        },
      },
      backgroundColor: "Transparent",
      botAvatarImage: "./static/img/logos/bot-logo-05.svg",
      botAvatarBackgroundColor: "#fff",
      botAvatarInitials: "BF",
      bubbleBorderRadius: 32,
      bubbleBackground: "#ffffff",
      bubbleTextColor: "#fff",
      bubbleBorderColor: "transparent",
      bubbleFromUserTextColor: "#fff",
      bubbleFromUserBackground: "#410817 !important",
      userAvatarImage: "./static/img/logos/User_Profile.svg",
      userAvatarInitials: "WC",
      userAvatarBackgroundColor: "#f3f2f2",
      bubbleFromUserBorderRadius: 32,
      accent: "#E10A17",
      avatarSize: 36,
      buttonBackgroundOnActive: "#f4b8c0",
      emojiSet: {
        ":sheep:": "🐑",
        "<3": "❤️",
      },
      hideUploadButton: true, // Enable upload button
      // uploadButtonColor: '#0078D4',
      sendBoxBackground: "transparent",
      sendBoxBorderTop: "1px solid rgba(255,255,255,0.1)",
      sendBoxTextColor: "#fff",
    }),
    [],
  );
  const handleFetchToken = useCallback(async () => {
    const authToken = sessionStorage.getItem("cva.jwt.access.token");
    const requestBody = {
      "user": {
        "id": "vijay.panwar@rr.com",
        "name": "Vijay Panwar"
      },
      "trustedOrigins": [
        "https://aabbb.com"
      ]
    };
    if (!token) {
      const res = await fetch(
        `${appConfig.baseUrl}/api/v2.0/litebot/tokens/generate?botId=${bot}&domainType=cvadirectline`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`, // Add the Authorization header
            'Content-Type': 'application/json'      // Optional: Set content type to JSON
          },
          body: JSON.stringify(requestBody)
        }
      );
      const { token } = await res.json();
      console.log("----------token:", token)
      setToken(token);
    }
  }, [setToken, token]);

  const handleMaximizeButtonClick = useCallback(() => {
    setLoaded(true);
    setMinimized(false);
    setNewMessage(false);
  }, []);

  const handleMinimizeButtonClick = useCallback(() => {
    setMinimized(true);
    setNewMessage(false);
  }, []);

  const handleExpandButtonClick = useCallback(() => {
    setIsExpanded(!isExpanded); // Toggle expansion
  }, [isExpanded]);

  const handleSwitchButtonClick = useCallback(() => {
    setSide(side === 'left' ? 'right' : 'left');
  }, [side]);

  // Voice recognition
  const handleVoiceButtonClick = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening]);

  const startListening = () => {
    setIsListening(true);
    // SpeechRecognition setup
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onresult = (event: { results: { [x: string]: { transcript: any; }[]; }; resultIndex: string | number; }) => {
      const transcript = event.results[event.resultIndex][0].transcript;
      console.log("Recognized Speech: ", transcript);
      sendToBot(transcript); // Send speech as text to bot
    };

    recognition.onend = () => {
      setIsListening(false); // Reset listening state
    };

    recognition.start();
  };

  const stopListening = () => {
    setIsListening(false);
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.stop();
    }
  };

  const sendToBot = (message: any) => {
    setMessage(message);
    setSendMessage(true);
    handleExpandButtonClick()
  };

  return (
    <div className="minimizable-web-chat">
      {loaded && (
        <div
          className={classNames(
            side === 'left' ? 'chat-box left' : 'chat-box right',
            minimized ? 'hide' : '',
            isExpanded ? 'expanded' : 'collapse' // Apply expanded class when expanded
          )}
        >

          <header>
            <div className="chat-brand-logo">
              <img src={appConfig.imagesUrl + "/logos/virgin-logo-modified.png"} alt="Company Logo" />
              <div className="chat-brand-txt"><span>Tech Concierge</span></div>
            </div>

            <div className="filler" />
            {/* <button className="info-btn" onClick={() => setShowBotInfo(true)} type="button">
              <i className="bi bi-info-circle"></i>
            </button> */}
            <button className="expand" onClick={handleExpandButtonClick} type="button">
              <i className={!isExpanded ? 'bi-arrows-angle-expand' : 'bi-arrows-angle-contract'} ></i>
            </button>
          </header>
          {sessionExpired && (
            <div
              style={{
                margin: "10px",
                padding: "10px",
                borderRadius: "8px",
                backgroundColor: "#fff3cd",
                border: "1px solid #ffeeba",
                color: "#856404",
                fontSize: "14px"
              }}
            >⚠️ Your session has expired. Please refresh the page and start a new conversation.</div>
          )}
          {/* {showBotInfo ? (
            <div className="bot-info-screen">
              <div className="info-header">
                <button className="back-btn" onClick={() => setShowBotInfo(false)} >
                  <i className="bi bi-arrow-left"></i>
                </button>
                <h2>Tech Concierge Information</h2>
              </div>
              <div className="info-card">
                <i className="bi bi-info-circle-fill"></i>
                <p>Tech Concierge is an IT AI assistant to request a KB article, create or update an incident, check incident status, or get redirected to connect to a live support agent.</p>
              </div>
            </div>
          ) : ( */}
            <WebChat
              className="react-web-chat"
              onFetchToken={handleFetchToken}
              store={store}
              styleOptions={{
                ...styleOptions,
                sendBoxPlaceholder: isInputDisabled
                  ? 'Input disabled due to policy…'
                  : 'Type your message…',
                sendBoxTextColor: isInputDisabled ? '#9aa0a6' : 'black',
                hideUploadButton: isInputDisabled ? true : styleOptions.hideUploadButton
              }}
              styleSet={styleSet}
              token={token}
              sendMessage={sendMessage}
              setSendMessage={(value) => { setSendMessage(value) }}
              message={message}
              disabled={isInputDisabled}
            />
          {/* )} */}
        </div>
      )}
    </div>
  );
}

export default memo(MinimizableWebChat);
